import { useState, useEffect } from 'react'

interface OrderItem {
  id?: number
  orderNumber: string
  productName: string
  variant: string | null
  sku: string | null
  imageUrl: string | null
  quantity: number
}

interface Order {
  orderNumber: string
  platform: string
  storeName: string
  buyer: string
  total: string
  quantity: string
  courier: string
  warehouse: string | null
  status: 'NEW' | 'PROCESSING' | 'SHIPPED' | 'CANCELLED'
  printedExpress: boolean
  printedPicklist: boolean
  creationTime: string | null
  paymentTime: string | null
  cancelEstimation: string | null
  items: OrderItem[]
  rawJson: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/orders')
      if (!response.ok) throw new Error('Failed to fetch orders')
      
      const ordersData = await response.json()
      setOrders(ordersData)
      setError(null)
    } catch (err) {
      console.error('Orders fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === 'all' || order.status === filter.toUpperCase()
    const matchesSearch = searchTerm === '' || 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.storeName.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesFilter && matchesSearch
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return '#007bff'
      case 'PROCESSING': return '#ffc107'
      case 'SHIPPED': return '#28a745'
      case 'CANCELLED': return '#dc3545'
      default: return '#6c757d'
    }
  }

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount)
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num)
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Loading Orders...</h1>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Orders Error</h1>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={fetchOrders}>Retry</button>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>📦 Orders ({filteredOrders.length})</h1>
        <button 
          onClick={fetchOrders}
          style={{ padding: '0.5rem 1rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
        />
        
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Orders Table */}
      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Order Number</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Buyer</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Store</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Total</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Qty</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Courier</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Created</th>
                <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Print Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.orderNumber} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '1rem' }}>
                    <strong>{order.orderNumber}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>{order.platform}</small>
                  </td>
                  <td style={{ padding: '1rem' }}>{order.buyer}</td>
                  <td style={{ padding: '1rem' }}>{order.storeName}</td>
                  <td style={{ padding: '1rem' }}>
                    <strong>{formatCurrency(order.total)}</strong>
                  </td>
                  <td style={{ padding: '1rem' }}>{order.quantity}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      color: 'white',
                      background: getStatusColor(order.status)
                    }}>
                      {order.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>{order.courier}</td>
                  <td style={{ padding: '1rem' }}>
                    {new Date(order.createdAt).toLocaleDateString('id-ID')}
                    <br />
                    <small style={{ color: '#6c757d' }}>
                      {new Date(order.createdAt).toLocaleTimeString('id-ID')}
                    </small>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '0.875rem' }}>
                      <div style={{ color: order.printedExpress ? '#28a745' : '#dc3545' }}>
                        {order.printedExpress ? '✅' : '❌'} Express
                      </div>
                      <div style={{ color: order.printedPicklist ? '#28a745' : '#dc3545' }}>
                        {order.printedPicklist ? '✅' : '❌'} Picklist
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
            No orders found matching your criteria.
          </div>
        )}
      </div>

      {/* Summary */}
      <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h4>Total Orders</h4>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>{orders.length}</p>
        </div>
        
        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h4>New Orders</h4>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
            {orders.filter(o => o.status === 'NEW').length}
          </p>
        </div>
        
        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h4>Processing</h4>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffc107' }}>
            {orders.filter(o => o.status === 'PROCESSING').length}
          </p>
        </div>
        
        <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h4>Shipped</h4>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
            {orders.filter(o => o.status === 'SHIPPED').length}
          </p>
        </div>
      </div>
    </div>
  )
}
